import { data, find, findAll } from './dom';

// Slika koju galerija prikazuje: adresa, najveća verzija za uvećanje, opis, natpis uvećanja i da li je primer štampe.
interface View {
  readonly src: string;
  readonly zoom: string;
  readonly alt: string;
  readonly caption: string;
  readonly sample: boolean;
}

// Galerija stranice proizvoda. Pilula boje i sličica bez skripte otvaraju svoju sliku; ovde menjaju veliku sliku u belom
// okviru, a uvećanje (lightbox), oznaka „Primer štampe“, izabrana boja i prekidač „Sa logom / Bez loga“ prate izbor.
export function enhanceGallery(): void {
  const gallery = document.querySelector('[data-js="gallery"]');
  const stage = gallery?.querySelector('[data-js="lightbox-open"]');
  if (!(gallery instanceof HTMLElement) || !(stage instanceof HTMLAnchorElement)) return;

  const image = find(stage, 'gallery-image', HTMLImageElement);
  const sample = find(gallery, 'gallery-sample', HTMLElement);
  const thumbs = findAll(gallery, 'gallery-thumb', HTMLAnchorElement);
  const colors = findAll(document, 'gallery-color', HTMLAnchorElement);
  const logos = findAll(gallery, 'gallery-logo', HTMLButtonElement);
  const toggle = gallery.querySelector('[data-js="gallery-toggle"]');
  const chosen = findAll(document, 'gallery-chosen', HTMLElement);
  let withLogo = true;

  const show = (view: View): void => {
    image.src = view.src;
    image.alt = view.alt;
    stage.href = view.zoom;
    stage.dataset['caption'] = view.caption;
    sample.hidden = !view.sample;
  };

  // Uz brendiranu sliku boja ima i fotografiju bez loga, pa se samo tada vidi prekidač. Boja bez nje ima prazan atribut
  // (link prolazi kroz tag helper, pa null vrednost ostaje kao prazan atribut).
  const hasPlain = (color: HTMLElement): boolean => (color.dataset['plainSrc'] ?? '') !== '';
  const showToggle = (visible: boolean): void => {
    if (toggle instanceof HTMLElement) toggle.hidden = !visible;
  };

  const colorView = (color: HTMLAnchorElement): View =>
    withLogo || !hasPlain(color)
      ? {
          src: data(color, 'src'),
          zoom: color.href,
          alt: data(color, 'alt'),
          caption: data(color, 'caption'),
          sample: data(color, 'sample') === 'true',
        }
      : {
          src: data(color, 'plainSrc'),
          zoom: data(color, 'plainZoom'),
          alt: data(color, 'plainAlt'),
          caption: data(color, 'plainCaption'),
          sample: false,
        };

  const current = (): HTMLAnchorElement | undefined =>
    colors.find(color => color.getAttribute('aria-current') === 'true');

  colors.forEach(color =>
    $(color).on('click', event => {
      event.preventDefault();
      colors.forEach(other => other.removeAttribute('aria-current'));
      color.setAttribute('aria-current', 'true');
      thumbs.forEach(thumb => thumb.removeAttribute('aria-current'));
      chosen.forEach(element => {
        element.textContent = data(color, 'color');
      });
      show(colorView(color));
      showToggle(hasPlain(color));
    })
  );

  thumbs.forEach(thumb =>
    $(thumb).on('click', event => {
      event.preventDefault();
      show({
        src: data(thumb, 'src'),
        zoom: thumb.href,
        alt: thumb.querySelector('img')?.alt ?? '',
        caption: data(thumb, 'caption'),
        sample: data(thumb, 'sample') === 'true',
      });
      thumbs.forEach(other => other.removeAttribute('aria-current'));
      thumb.setAttribute('aria-current', 'true');
      showToggle(false);
    })
  );

  logos.forEach(button =>
    $(button).on('click', () => {
      withLogo = data(button, 'logo') === 'true';
      logos.forEach(other => other.setAttribute('aria-pressed', String(other === button)));
      const color = current();
      if (color) show(colorView(color));
    })
  );

  const first = current();
  showToggle(first !== undefined && hasPlain(first));
}
